import React, { useState } from "react";
import {
  Modal as MuiModal,
  Backdrop,
  Fade,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SoftBox from "../../components/SoftBox";
import SoftButton from "../../components/SoftButton";
import { DEFAULT_COLUMNS } from "./useColumnPreferences";
import { TEAM_COLOR_PALETTE, setTeamColor } from "./teamColorStore";
import { UseTeamColorMap } from "../../hooks/teams";

/* =========================================================================
 * CustomizeViewModal
 *
 * Two-tab modal for the Players page:
 *   - Tab "Customize layout" — toggle column visibility (locked rows excluded).
 *   - Tab "Manage teams"     — create / rename / delete teams + bulk assign players.
 *
 * Visual language matches the existing glass modals in the app
 * (FeedPage settings + SubWorkspaceDialog) but with tighter typography:
 *   - 17px / 700 headlines (vs the existing 20px h4)
 *   - 11px / 600 / 0.08em uppercase section labels for clear hierarchy
 *   - 13–14px body copy with slightly more line-height for readability
 *   - Brand-blue (#2563EB) primary, soft translucent surfaces inside
 * ========================================================================= */

const FONT = `"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif`;

// Reusable token map for translucent surfaces inside the glass shell.
const T = {
  primary: "#2563EB",
  primarySoft: "rgba(37,99,235,0.18)",
  text: "#0F172A",
  textMuted: "#475467",
  textFaint: "#98A2B3",
  hairline: "rgba(15,23,42,0.10)",
  innerSurface: "rgba(15,23,42,0.03)",
  innerSurfaceHover: "rgba(15,23,42,0.06)",
  danger: "#EF4444",
};

/* ---- Reusable bits ----------------------------------------------------- */

const SectionLabel = ({ children, hint }) => (
  <SoftBox sx={{ marginBottom: hint ? "4px" : "12px" }}>
    <SoftBox
      component="div"
      sx={{
        fontFamily: FONT,
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: T.textFaint,
      }}
    >
      {children}
    </SoftBox>
    {hint && (
      <SoftBox
        sx={{
          marginTop: "4px",
          fontFamily: FONT,
          fontSize: "12.5px",
          fontWeight: 400,
          color: T.textMuted,
          lineHeight: 1.5,
        }}
      >
        {hint}
      </SoftBox>
    )}
  </SoftBox>
);

const Switch = ({ checked, disabled, onChange }) => (
  <SoftBox
    component="button"
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={disabled ? undefined : onChange}
    disabled={disabled}
    sx={{
      width: "36px",
      height: "20px",
      borderRadius: "999px",
      border: 0,
      padding: 0,
      cursor: disabled ? "default" : "pointer",
      position: "relative",
      background: checked ? T.primary : "rgba(15,23,42,0.18)",
      opacity: disabled ? 0.4 : 1,
      transition: "background 160ms ease",
      flexShrink: 0,
      "&::after": {
        content: '""',
        position: "absolute",
        top: "2px",
        left: checked ? "18px" : "2px",
        width: "16px",
        height: "16px",
        borderRadius: "50%",
        background: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
        transition: "left 160ms ease",
      },
    }}
  />
);

const TabButton = ({ active, onClick, children, count }) => (
  <SoftBox
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      flex: 1,
      height: "36px",
      borderRadius: "8px",
      border: 0,
      cursor: "pointer",
      fontFamily: FONT,
      fontSize: "13px",
      fontWeight: 600,
      letterSpacing: "0.01em",
      color: active ? "#fff" : T.textMuted,
      background: active ? T.primary : "transparent",
      transition: "background 140ms ease, color 140ms ease",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      "&:hover": active ? undefined : { color: T.text },
    }}
  >
    {children}
    {typeof count === "number" && (
      <SoftBox
        component="span"
        sx={{
          fontFamily: FONT,
          fontSize: "11px",
          fontWeight: 700,
          padding: "1px 8px",
          borderRadius: "999px",
          background: active ? "rgba(255,255,255,0.20)" : "rgba(15,23,42,0.06)",
          color: active ? "#fff" : T.textMuted,
        }}
      >
        {count}
      </SoftBox>
    )}
  </SoftBox>
);

const SmallIconBtn = ({ onClick, label, danger, children }) => (
  <SoftBox
    component="button"
    type="button"
    onClick={onClick}
    aria-label={label}
    sx={{
      width: "30px",
      height: "30px",
      borderRadius: "8px",
      border: 0,
      padding: 0,
      cursor: "pointer",
      background: "transparent",
      color: danger ? T.danger : T.textMuted,
      display: "grid",
      placeItems: "center",
      transition: "background 120ms ease, color 120ms ease",
      "&:hover": {
        background: danger ? "rgba(239,68,68,0.12)" : "rgba(15,23,42,0.06)",
        color: danger ? T.danger : T.text,
      },
    }}
  >
    {children}
  </SoftBox>
);

/* ---- Tab 1 — Customize layout ----------------------------------------- */

function CustomizeLayoutTab({ columnPrefs }) {
  const { columns, toggleVisible } = columnPrefs;
  return (
    <SoftBox>
      <SectionLabel hint="Choose which columns appear in the players table.">
        Columns
      </SectionLabel>

      <SoftBox
        sx={{
          marginTop: "14px",
          background: T.innerSurface,
          border: `1px solid ${T.hairline}`,
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        {columns.map((col, i) => (
          <SoftBox
            key={col.key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "12px 14px",
              borderTop: i === 0 ? "none" : `1px solid ${T.hairline}`,
            }}
          >
            <SoftBox
              sx={{
                fontFamily: FONT,
                fontSize: "14px",
                fontWeight: 500,
                color: T.text,
                flex: 1,
              }}
            >
              {col.label}
            </SoftBox>
            {col.locked ? (
              <SoftBox
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  fontFamily: FONT,
                  fontSize: "11px",
                  fontWeight: 500,
                  color: T.textFaint,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Required
              </SoftBox>
            ) : (
              <Switch
                checked={col.visible}
                onChange={() => toggleVisible(col.key)}
              />
            )}
          </SoftBox>
        ))}
      </SoftBox>
    </SoftBox>
  );
}

/* ---- Tab 2 — Manage teams --------------------------------------------- */

function ManageTeamsTab({ teamsApi, users, assignUserToTeam }) {
  const { teams, counts, create, rename, remove } = teamsApi;
  const [newTeamName, setNewTeamName] = useState("");
  const [editingTeam, setEditingTeam] = useState(null); // team currently in rename mode
  const [editingValue, setEditingValue] = useState("");
  // Team color picker state — which team's swatch is currently open.
  // Color map is read from Redux so a save reflects everywhere instantly.
  const [colorPickerFor, setColorPickerFor] = useState(null);
  const teamColors = UseTeamColorMap();

  // Bulk-assign state
  const [bulkTeam, setBulkTeam] = useState("");
  const [bulkUserIds, setBulkUserIds] = useState([]);
  const [bulkSearch, setBulkSearch] = useState("");

  const onCreate = () => {
    if (create(newTeamName)) setNewTeamName("");
  };

  const startRename = (name) => {
    setEditingTeam(name);
    setEditingValue(name);
  };

  const commitRename = () => {
    if (!editingTeam) return;
    const oldName = editingTeam;
    const newName = editingValue.trim();
    if (newName && newName !== oldName) {
      // Server cascades the rename across every user that had the old name,
      // so no per-user reassignment is needed from the client.
      rename(oldName, newName);
    }
    setEditingTeam(null);
    setEditingValue("");
  };

  const onDelete = (name) => {
    // Server archives the team AND unassigns its members atomically.
    remove(name);
  };

  const onBulkAssign = async () => {
    if (!bulkTeam || bulkUserIds.length === 0) return;
    await Promise.all(bulkUserIds.map((id) => assignUserToTeam(id, bulkTeam)));
    setBulkUserIds([]);
  };

  const eligibleUsers = users.filter((u) => {
    if (u.team === bulkTeam) return false;
    if (!bulkSearch.trim()) return true;
    const q = bulkSearch.trim().toLowerCase();
    const full = `${u.firstName || ""} ${u.lastName || ""}`.trim().toLowerCase();
    return (
      full.includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.team || "").toLowerCase().includes(q)
    );
  });

  return (
    <SoftBox>
      {/* Existing teams */}
      <SectionLabel hint="Group players into teams. Teams show on the leaderboard TV.">
        Teams
      </SectionLabel>

      {teams.length === 0 ? (
        <SoftBox
          sx={{
            marginTop: "14px",
            padding: "20px 14px",
            textAlign: "center",
            background: T.innerSurface,
            border: `1px dashed ${T.hairline}`,
            borderRadius: "10px",
            fontFamily: FONT,
            fontSize: "13px",
            color: T.textMuted,
          }}
        >
          No teams yet. Create your first team below.
        </SoftBox>
      ) : (
        <SoftBox
          sx={{
            marginTop: "14px",
            background: T.innerSurface,
            border: `1px solid ${T.hairline}`,
            borderRadius: "10px",
            // overflow: visible so the color picker popover on each team row
            // can escape the container (it was previously clipped, especially
            // for the 2nd+ rows where the popover extends past the bottom edge).
            overflow: "visible",
          }}
        >
          {teams.map((name, i) => {
            const isEditing = editingTeam === name;
            const count = counts[name] || 0;
            return (
              <SoftBox
                key={name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 12px",
                  borderTop: i === 0 ? "none" : `1px solid ${T.hairline}`,
                }}
              >
                {/* Color swatch — click to open the palette popover.
                    Default swatch shows brand-blue (T.primary) when no color
                    has been picked yet, so the row never looks blank. */}
                <SoftBox sx={{ position: "relative", flexShrink: 0 }}>
                  <SoftBox
                    component="button"
                    type="button"
                    onClick={() =>
                      setColorPickerFor(colorPickerFor === name ? null : name)
                    }
                    aria-label={`Pick color for ${name}`}
                    sx={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: "2px solid rgba(255,255,255,0.85)",
                      padding: 0,
                      cursor: "pointer",
                      background: teamColors[name] || T.primary,
                      transition: "transform 120ms ease, box-shadow 120ms ease",
                      boxShadow:
                        colorPickerFor === name
                          ? "0 0 0 2px rgba(37,99,235,0.6)"
                          : "0 1px 3px rgba(16,24,40,0.18)",
                      "&:hover": { transform: "scale(1.08)" },
                    }}
                  />
                  {colorPickerFor === name && (
                    <SoftBox
                      sx={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        left: 0,
                        zIndex: 10,
                        background: "#fff",
                        border: `1px solid ${T.hairline}`,
                        borderRadius: "10px",
                        padding: "10px",
                        boxShadow: "0 12px 28px rgba(16,24,40,0.18)",
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 22px)",
                        gap: "8px",
                      }}
                    >
                      {TEAM_COLOR_PALETTE.map((hex) => {
                        const active = (teamColors[name] || "").toLowerCase() === hex.toLowerCase();
                        return (
                          <SoftBox
                            key={hex}
                            component="button"
                            type="button"
                            aria-label={`Use ${hex}`}
                            onClick={() => {
                              setTeamColor(name, hex);
                              setColorPickerFor(null);
                            }}
                            sx={{
                              width: "22px",
                              height: "22px",
                              borderRadius: "50%",
                              padding: 0,
                              cursor: "pointer",
                              background: hex,
                              border: active ? "2px solid #fff" : "2px solid transparent",
                              boxShadow: active
                                ? `0 0 0 2px ${hex}, 0 0 0 4px rgba(15,23,42,0.15)`
                                : "0 1px 2px rgba(16,24,40,0.18)",
                              transition: "transform 100ms ease",
                              "&:hover": { transform: "scale(1.12)" },
                            }}
                          />
                        );
                      })}
                    </SoftBox>
                  )}
                </SoftBox>
                {isEditing ? (
                  <input
                    ref={(el) => { if (el) el.focus(); }}
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRename();
                      if (e.key === "Escape") {
                        setEditingTeam(null);
                        setEditingValue("");
                      }
                    }}
                    style={{
                      flex: 1,
                      background: "#F6F7F9",
                      border: `1px solid ${T.primary}`,
                      borderRadius: "6px",
                      padding: "4px 8px",
                      fontFamily: FONT,
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#1d2939",
                      outline: "none",
                    }}
                  />
                ) : (
                  <SoftBox
                    sx={{
                      flex: 1,
                      fontFamily: FONT,
                      fontSize: "14px",
                      fontWeight: 500,
                      color: T.text,
                    }}
                  >
                    {name}
                  </SoftBox>
                )}
                <SoftBox
                  sx={{
                    fontFamily: FONT,
                    fontSize: "11px",
                    fontWeight: 500,
                    color: T.textFaint,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {count} {count === 1 ? "player" : "players"}
                </SoftBox>
                {!isEditing && (
                  <>
                    <SmallIconBtn label="Rename" onClick={() => startRename(name)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </SmallIconBtn>
                    <SmallIconBtn danger label="Delete" onClick={() => onDelete(name)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6M14 11v6" />
                      </svg>
                    </SmallIconBtn>
                  </>
                )}
              </SoftBox>
            );
          })}
        </SoftBox>
      )}

      {/* Create new team */}
      <SoftBox sx={{ marginTop: "14px", display: "flex", gap: "8px" }}>
        <input
          value={newTeamName}
          onChange={(e) => setNewTeamName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onCreate();
          }}
          placeholder="New team name"
          style={{
            flex: 1,
            height: "40px",
            background: T.innerSurface,
            border: `1px solid ${T.hairline}`,
            borderRadius: "8px",
            padding: "0 12px",
            fontFamily: FONT,
            fontSize: "14px",
            fontWeight: 500,
            color: "#1d2939",
            outline: "none",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = T.primary;
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = T.hairline;
          }}
        />
        <SoftBox
          component="button"
          type="button"
          onClick={onCreate}
          disabled={!newTeamName.trim()}
          sx={{
            height: "40px",
            padding: "0 16px",
            border: 0,
            borderRadius: "8px",
            cursor: newTeamName.trim() ? "pointer" : "default",
            background: newTeamName.trim() ? T.primary : "rgba(15,23,42,0.10)",
            color: "#fff",
            fontFamily: FONT,
            fontSize: "13px",
            fontWeight: 600,
            transition: "background 120ms ease",
            opacity: newTeamName.trim() ? 1 : 0.6,
          }}
        >
          + Create team
        </SoftBox>
      </SoftBox>

      {/* Bulk assignment */}
      {teams.length > 0 && (
        <>
          <SoftBox sx={{ height: "1px", background: T.hairline, margin: "22px 0 18px" }} />

          <SectionLabel hint="Drop multiple players into a team in one go.">
            Bulk assign players
          </SectionLabel>

          <SoftBox sx={{ marginTop: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>
            <SoftBox sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <SoftBox
                sx={{
                  width: "70px",
                  fontFamily: FONT,
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: T.textMuted,
                }}
              >
                Team
              </SoftBox>
              <select
                value={bulkTeam}
                onChange={(e) => setBulkTeam(e.target.value)}
                style={{
                  flex: 1,
                  height: "40px",
                  background: T.innerSurface,
                  border: `1px solid ${T.hairline}`,
                  borderRadius: "8px",
                  padding: "0 12px",
                  fontFamily: FONT,
                  fontSize: "14px",
                  color: bulkTeam ? "#1d2939" : T.textFaint,
                  outline: "none",
                  appearance: "none",
                }}
              >
                <option value="" style={{ color: "#000" }}>Select a team…</option>
                {teams.map((t) => (
                  <option key={t} value={t} style={{ color: "#000" }}>{t}</option>
                ))}
              </select>
            </SoftBox>

            <SoftBox sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
              <SoftBox
                sx={{
                  width: "70px",
                  paddingTop: "10px",
                  fontFamily: FONT,
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: T.textMuted,
                }}
              >
                Players
              </SoftBox>
              <SoftBox
                sx={{
                  flex: 1,
                  background: T.innerSurface,
                  border: `1px solid ${T.hairline}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {/* Search inside the player picker — quick filter by name / email */}
                {bulkTeam && users.length > 6 && (
                  <SoftBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 10px",
                      borderBottom: `1px solid ${T.hairline}`,
                      background: "rgba(15,23,42,0.03)",
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textFaint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                    </svg>
                    <input
                      value={bulkSearch}
                      onChange={(e) => setBulkSearch(e.target.value)}
                      placeholder="Search by name or email"
                      style={{
                        flex: 1,
                        background: "transparent",
                        border: 0,
                        outline: "none",
                        color: "#1d2939",
                        fontFamily: FONT,
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                    />
                    {bulkSearch && (
                      <SoftBox
                        component="button"
                        type="button"
                        onClick={() => setBulkSearch("")}
                        aria-label="Clear search"
                        sx={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          padding: 0,
                          border: 0,
                          background: "rgba(15,23,42,0.06)",
                          color: T.text,
                          cursor: "pointer",
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          "&:hover": { background: "rgba(15,23,42,0.10)" },
                        }}
                      >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </SoftBox>
                    )}
                  </SoftBox>
                )}
                <SoftBox sx={{ maxHeight: "180px", overflowY: "auto" }}>
                {eligibleUsers.length === 0 ? (
                  <SoftBox
                    sx={{
                      padding: "12px 14px",
                      fontFamily: FONT,
                      fontSize: "13px",
                      color: T.textFaint,
                    }}
                  >
                    {(() => {
                      if (!bulkTeam) return "Pick a team first.";
                      if (bulkSearch) return `No matches for "${bulkSearch}".`;
                      return "Everyone is already in this team.";
                    })()}
                  </SoftBox>
                ) : (
                  eligibleUsers.map((u, i) => {
                    const checked = bulkUserIds.includes(u.userId);
                    return (
                      <SoftBox
                        key={u.userId}
                        component="button"
                        type="button"
                        onClick={() =>
                          setBulkUserIds((prev) =>
                            checked ? prev.filter((id) => id !== u.userId) : [...prev, u.userId],
                          )
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          width: "100%",
                          padding: "8px 12px",
                          background: "transparent",
                          border: 0,
                          borderTop: i === 0 ? "none" : `1px solid ${T.hairline}`,
                          cursor: "pointer",
                          textAlign: "left",
                          "&:hover": { background: "rgba(15,23,42,0.04)" },
                        }}
                      >
                        <SoftBox
                          sx={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "4px",
                            border: `1.5px solid ${checked ? T.primary : T.hairline}`,
                            background: checked ? T.primary : "transparent",
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            transition: "background 120ms ease, border-color 120ms ease",
                          }}
                        >
                          {checked && (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </SoftBox>
                        <SoftBox
                          sx={{
                            flex: 1,
                            fontFamily: FONT,
                            fontSize: "13px",
                            fontWeight: 500,
                            color: T.text,
                          }}
                        >
                          {u.firstName} {u.lastName}
                        </SoftBox>
                        {u.team && (
                          <SoftBox
                            sx={{
                              fontFamily: FONT,
                              fontSize: "11px",
                              color: T.textFaint,
                            }}
                          >
                            in {u.team}
                          </SoftBox>
                        )}
                      </SoftBox>
                    );
                  })
                )}
                </SoftBox>
              </SoftBox>
            </SoftBox>

            <SoftBox sx={{ display: "flex", justifyContent: "flex-end", marginTop: "4px" }}>
              <SoftBox
                component="button"
                type="button"
                onClick={onBulkAssign}
                disabled={!bulkTeam || bulkUserIds.length === 0}
                sx={{
                  height: "38px",
                  padding: "0 16px",
                  border: 0,
                  borderRadius: "8px",
                  cursor: bulkTeam && bulkUserIds.length > 0 ? "pointer" : "default",
                  background:
                    bulkTeam && bulkUserIds.length > 0 ? T.primary : "rgba(15,23,42,0.10)",
                  color: "#fff",
                  fontFamily: FONT,
                  fontSize: "13px",
                  fontWeight: 600,
                  opacity: bulkTeam && bulkUserIds.length > 0 ? 1 : 0.6,
                  transition: "background 120ms ease",
                }}
              >
                Add {bulkUserIds.length || ""} to team
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </>
      )}
    </SoftBox>
  );
}

/* ---- Modal shell ------------------------------------------------------- */

const SHELL = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: { mini: "92%", sm: "560px" },
  background: "#FFFFFF",
  boxShadow:
    "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
  borderRadius: "8px",
  border: "1px solid rgba(15,23,42,0.10)",
  overflow: "hidden",
  fontFamily: FONT,
};

export default function CustomizeViewModal({
  open,
  onClose,
  columnPrefs,
  teamsApi,
  users,
  assignUserToTeam,
}) {
  const [tab, setTab] = useState("layout");
  const {
    palette: {
      primary: { main: primaryColor },
    },
  } = useTheme();

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 240,
        // Match the app's Dialog overlay ("dims just enough, never dark"):
        // MuiModal doesn't inherit the MuiDialog theme backdrop, so set it here.
        sx: {
          backgroundColor: "rgba(15,23,42,0.16)",
          backdropFilter: "blur(3px)",
          WebkitBackdropFilter: "blur(3px)",
        },
      }}
      closeAfterTransition
    >
      <Fade in={open}>
        <SoftBox sx={SHELL}>
          {/* Header */}
          <SoftBox
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "18px 22px 14px",
              borderBottom: `1px solid ${T.hairline}`,
            }}
          >
            <SoftBox>
              <SoftBox
                sx={{
                  fontFamily: FONT,
                  fontSize: "17px",
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                  color: T.text,
                }}
              >
                Customize view
              </SoftBox>
              <SoftBox
                sx={{
                  marginTop: "2px",
                  fontFamily: FONT,
                  fontSize: "12.5px",
                  fontWeight: 400,
                  color: T.textFaint,
                }}
              >
                Layout settings and team management for this table.
              </SoftBox>
            </SoftBox>
            <SoftButton
              color="light"
              onClick={onClose}
              sx={{
                "& svg": { fontSize: "1.1rem !important" },
                borderRadius: "50%",
                minWidth: "32px",
                minHeight: "32px",
                width: "32px",
                height: "32px",
                padding: 0,
                background: "rgba(15,23,42,0.05)!important",
                border: `1px solid ${T.hairline}`,
                color: `${T.text} !important`,
              }}
              aria-label="Close"
            >
              <CloseIcon />
            </SoftButton>
          </SoftBox>

          {/* Tabs */}
          <SoftBox
            sx={{
              padding: "12px 22px 0",
            }}
          >
            <SoftBox
              sx={{
                display: "flex",
                gap: "4px",
                padding: "4px",
                background: T.innerSurface,
                border: `1px solid ${T.hairline}`,
                borderRadius: "10px",
              }}
            >
              <TabButton active={tab === "layout"} onClick={() => setTab("layout")}>
                Customize layout
              </TabButton>
              <TabButton
                active={tab === "teams"}
                onClick={() => setTab("teams")}
                count={teamsApi.teams.length}
              >
                Manage teams
              </TabButton>
            </SoftBox>
          </SoftBox>

          {/* Body */}
          <SoftBox
            sx={{
              padding: "20px 22px 4px",
              maxHeight: "min(70vh, 560px)",
              overflowY: "auto",
            }}
          >
            {tab === "layout" ? (
              <CustomizeLayoutTab columnPrefs={columnPrefs} />
            ) : (
              <ManageTeamsTab
                teamsApi={teamsApi}
                users={users}
                assignUserToTeam={assignUserToTeam}
              />
            )}
          </SoftBox>

          {/* Footer */}
          <SoftBox
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
              padding: "14px 22px 18px",
              borderTop: `1px solid ${T.hairline}`,
              background: "rgba(15,23,42,0.03)",
            }}
          >
            <SoftBox
              component="button"
              type="button"
              onClick={onClose}
              sx={{
                height: "40px",
                padding: "0 20px",
                background: primaryColor,
                border: 0,
                borderRadius: "8px",
                color: "#FFFFFF",
                fontFamily: FONT,
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "filter 120ms ease",
                "&:hover": { filter: "brightness(0.96)" },
              }}
            >
              Done
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </Fade>
    </MuiModal>
  );
}
